// src/pages/VendorInsights.js
// Cross-event vendor performance dashboard. Pure derivation from the
// EventVendor tag rows already captured during normal use; no new schema.
// Mirrors MealInsights — single page, top-25 vendors, category breakdown.
//
// Per docs/strategy/competitive-positioning.md, this is the
// "marketplace-without-the-marketplace" play — analytics on data we own,
// no inventory war with Planit.ng / Vowthread.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const formatNumber = (n) => new Intl.NumberFormat('en-NG').format(Number(n || 0));
const formatNaira = (n) => new Intl.NumberFormat('en-NG', {
	style: 'currency', currency: 'NGN', maximumFractionDigits: 0,
}).format(Number(n || 0));

const CATEGORY_LABEL = {
	cake: 'Cake', photo: 'Photography', video: 'Videography', dj: 'DJ',
	mc: 'MC', decor: 'Decor', flowers: 'Flowers', makeup: 'Makeup',
	aso_ebi: 'Aso-ebi', security: 'Security', logistics: 'Logistics',
	rentals: 'Rentals', other: 'Other',
};

const VendorInsights = () => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		let alive = true;
		(async () => {
			try {
				const res = await api.get('/vendors/insights');
				if (alive) setData(res.data);
			} catch (err) {
				if (alive) setError(err.response?.data?.msg || 'Failed to load vendor insights.');
			} finally {
				if (alive) setLoading(false);
			}
		})();
		return () => { alive = false; };
	}, []);

	const maxEvents = useMemo(() => {
		if (!data?.topVendors?.length) return 1;
		return Math.max(...data.topVendors.map((v) => v.eventCount), 1);
	}, [data]);

	return (
		<div className="ev-user-shell">
			<header className="ev-admin-topbar">
				<div className="ev-admin-topbar-inner">
					<div className="ev-admin-topbar-left">
						<div className="ev-brand-lockup">
							<img src="/images/eventory-logo.svg" alt="Eventory" />
						</div>
					</div>
					<div className="ev-admin-topbar-actions">
						<Link to="/dashboard" className="ev-soft-btn">My events</Link>
						<Link to="/account/vendors" className="ev-soft-btn">Manage roster</Link>
						<Link to="/account/meal-insights" className="ev-soft-btn">Meal insights</Link>
						<Link to="/account" className="ev-soft-btn">Account</Link>
					</div>
				</div>
			</header>

			<main className="ev-user-dash">
				<div className="ev-user-wrap">
					<div className="ev-admin-header">
						<div>
							<h2 className="ev-section-title">Vendor insights</h2>
							<div className="ev-subtle">
								Who you've used most, what you've paid, where to focus your repeat business.
								Built from the per-event vendor tags you've already added.
							</div>
						</div>
					</div>

					{error && <div className="ev-alert ev-alert-danger">{error}</div>}
					{loading && <div className="ev-subtle">Loading…</div>}

					{!loading && data && (
						<>
							<div className="ev-overview-grid ev-bottom-gap">
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Vendors in roster</span></div>
									<div className="ev-metric-value">{formatNumber(data.totalVendors)}</div>
								</div>
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Total engagements</span></div>
									<div className="ev-metric-value">{formatNumber(data.totalTags)}</div>
									<div className="ev-subtle" style={{ fontSize: 12, marginTop: 4 }}>vendor tags across events</div>
								</div>
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Lifetime vendor spend</span></div>
									<div className="ev-metric-value">{formatNaira(data.totalSpendNgn)}</div>
									<div className="ev-subtle" style={{ fontSize: 12, marginTop: 4 }}>only counts payments you logged</div>
								</div>
								<div className="ev-metric-card is-populated">
									<div className="ev-metric-title"><span>Categories used</span></div>
									<div className="ev-metric-value">{formatNumber(data.byCategory.length)}</div>
								</div>
							</div>

							{data.totalTags === 0 ? (
								<section className="ev-admin-section ev-flat-card">
									<div className="ev-empty">
										<strong>No vendor engagements yet.</strong>
										<div className="ev-subtle">
											Add vendors to your roster at <Link to="/account/vendors">My vendors</Link>, then tag who supplied what on each event from the event's overview page.
										</div>
									</div>
								</section>
							) : (
								<>
									<section className="ev-admin-section ev-flat-card">
										<h3 className="ev-zero-margin">Spend by category</h3>
										<div className="ev-subtle" style={{ marginBottom: 12 }}>
											How your event budget splits across vendor types.
										</div>
										<table className="ev-data-table">
											<thead>
												<tr>
													<th>Category</th>
													<th>Vendors</th>
													<th>Engagements</th>
													<th>Spend</th>
												</tr>
											</thead>
											<tbody>
												{data.byCategory.map((c) => (
													<tr key={c.category}>
														<td><strong>{CATEGORY_LABEL[c.category] || c.category}</strong></td>
														<td>{formatNumber(c.vendorCount)}</td>
														<td>{formatNumber(c.eventCount)}</td>
														<td>{formatNaira(c.totalSpendNgn)}</td>
													</tr>
												))}
											</tbody>
										</table>
									</section>

									<section className="ev-admin-section ev-flat-card">
										<h3 className="ev-zero-margin">Top vendors</h3>
										<div className="ev-subtle" style={{ marginBottom: 12 }}>
											Ranked by event count, then by spend. Your most-relied-on suppliers.
										</div>
										<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
											{data.topVendors.map((v, i) => {
												const barPct = Math.round((v.eventCount / maxEvents) * 100);
												return (
													<div
														key={v._id}
														style={{
															background: 'white',
															border: '1px solid rgba(17,17,19,0.06)',
															borderRadius: 10,
															padding: '12px 14px',
														}}
													>
														<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: 8 }}>
															<div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
																<span style={{ fontSize: 12, color: '#6B7280', fontWeight: 700, minWidth: 22 }}>
																	#{i + 1}
																</span>
																<strong style={{ fontSize: 15 }}>{v.name}</strong>
																<span className="ev-subtle" style={{ fontSize: 12 }}>· {CATEGORY_LABEL[v.category] || v.category}</span>
															</div>
															<div className="ev-subtle" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
																{v.eventCount} event{v.eventCount === 1 ? '' : 's'} · {formatNaira(v.totalSpendNgn)}
															</div>
														</div>
														<div style={{ height: 6, background: 'rgba(61, 30, 109, 0.08)', borderRadius: 999, overflow: 'hidden' }}>
															<div
																style={{
																	width: `${barPct}%`,
																	height: '100%',
																	background: 'linear-gradient(90deg, #3D1E6D 0%, #FB8601 100%)',
																	borderRadius: 999,
																}}
															/>
														</div>
														{(v.contactName || v.phone || v.instagram) && (
															<div className="ev-subtle" style={{ fontSize: 12, marginTop: 6 }}>
																{[v.contactName, v.phone, v.instagram].filter(Boolean).join(' · ')}
															</div>
														)}
													</div>
												);
											})}
										</div>
									</section>
								</>
							)}
						</>
					)}
				</div>
			</main>
		</div>
	);
};

export default VendorInsights;
